var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useRef } from "react";
/** Poll interval when Pusher already delivers live updates. */
export var FALLBACK_POLL_MS = 5000;
/**
 * Custom hook for polling data at regular intervals
 *
 * @example
 * ```tsx
 * const { isPolling } = usePolling({
 *   pollFn: async () => await fetchTransaction(token),
 *   shouldStop: (data) => ['completed', 'error'].includes(data.status),
 *   interval: 2000,
 *   enabled: true,
 *   onData: (data) => setInfo(data),
 * });
 * ```
 */
export function usePolling(_a) {
    var _this = this;
    var pollFn = _a.pollFn, shouldStop = _a.shouldStop, _b = _a.interval, interval = _b === void 0 ? 2000 : _b, _c = _a.enabled, enabled = _c === void 0 ? true : _c, _d = _a.immediate, immediate = _d === void 0 ? true : _d, onData = _a.onData, onError = _a.onError;
    var intervalRef = useRef(null);
    var isPollingRef = useRef(false);
    var mountedRef = useRef(true);
    var inFlightRef = useRef(false);
    // Use refs to store latest values without causing re-renders
    var pollFnRef = useRef(pollFn);
    var shouldStopRef = useRef(shouldStop);
    var onDataRef = useRef(onData);
    var onErrorRef = useRef(onError);
    // Update refs when values change
    useEffect(function () {
        pollFnRef.current = pollFn;
        shouldStopRef.current = shouldStop;
        onDataRef.current = onData;
        onErrorRef.current = onError;
    });
    useEffect(function () {
        mountedRef.current = true;
        return function () {
            mountedRef.current = false;
        };
    }, []);
    useEffect(function () {
        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
        if (!enabled) {
            isPollingRef.current = false;
            return;
        }
        isPollingRef.current = true;
        var poll = function () { return __awaiter(_this, void 0, void 0, function () {
            var result, error_1, err;
            var _a, _b;
            return __generator(this, function (_c) {
                switch (_c.label) {
                    case 0:
                        if (!mountedRef.current || !isPollingRef.current || inFlightRef.current) {
                            return [2 /*return*/];
                        }
                        inFlightRef.current = true;
                        _c.label = 1;
                    case 1:
                        _c.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, pollFnRef.current()];
                    case 2:
                        result = _c.sent();
                        if (!mountedRef.current || !isPollingRef.current) {
                            return [2 /*return*/];
                        }
                        if (result !== undefined) {
                            // Check if we should stop polling
                            if (shouldStopRef.current(result)) {
                                isPollingRef.current = false;
                                if (intervalRef.current) {
                                    clearInterval(intervalRef.current);
                                    intervalRef.current = null;
                                }
                            }
                            // Call onData callback if provided
                            (_a = onDataRef.current) === null || _a === void 0 ? void 0 : _a.call(onDataRef, result);
                        }
                        return [3 /*break*/, 5];
                    case 3:
                        error_1 = _c.sent();
                        if (mountedRef.current && isPollingRef.current) {
                            err = error_1 instanceof Error ? error_1 : new Error(String(error_1));
                            (_b = onErrorRef.current) === null || _b === void 0 ? void 0 : _b.call(onErrorRef, err);
                        }
                        return [3 /*break*/, 5];
                    case 4:
                        inFlightRef.current = false;
                        return [7 /*endfinally*/];
                    case 5: return [2 /*return*/];
                }
            });
        }); };
        // Poll immediately if requested
        if (immediate) {
            poll();
        }
        // Set up interval for continuous polling
        intervalRef.current = setInterval(function () {
            if (isPollingRef.current) {
                poll();
            }
        }, interval);
        // Cleanup on unmount or when dependencies change
        return function () {
            isPollingRef.current = false;
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [interval, enabled, immediate]);
    return {
        isPolling: isPollingRef.current,
    };
}
